import {
  Merchant,
  MerchantDataLoaderParams,
  MerchantDomain,
  PaginatedResponse,
  QueryResponse,
  SiteStats,
  DataLoaderParams
} from '@/types';
import gql from 'graphql-tag';
import { ApiService } from '@/services';

export class MerchantService extends ApiService {
  public async getSiteStats(
    params: DataLoaderParams,
    id: number
  ): Promise<QueryResponse<SiteStats[]>> {
    const query = gql`
      query siteStats(
        $first: Int!
        $page: Int!
        $filters: DataLoaderParams!
        $id: ID!
      ) {
        siteStats(first: $first, page: $page, filters: $filters, id: $id) {
          data {
            site_name
            network
            views
            ctr
            clicks
            commission
            ranking
            commission_rate
          }
          paginatorInfo {
            total
            perPage
            currentPage
            hasMorePages
          }
        }
      }
    `;
    return this.query<QueryResponse<SiteStats[]>>({
      query,
      variables: { ...params, id }
    });
  }

  public async mutateMerchant(merchant: Merchant): Promise<Merchant> {
    return merchant.id
      ? this.updateMerchant(merchant)
      : this.createMerchant(merchant);
  }

  public async updateMerchant(merchant: Merchant): Promise<Merchant> {
    const mutation = gql`
      mutation updateMerchant($merchant: MerchantInput) {
        updateMerchant(merchant: $merchant) {
          id
          merchant_name
          notes
          insights
          is_autoconfigured
          affiliate_links {
            id
            link_template
            affiliate_link_status
            affiliate_network {
              id
              network_name
            }
            merchant {
              id
            }
          }
          merchant_domains {
            id
            merchant_id
            merchant_domain
            merchant_domain_status
          }
        }
      }
    `;
    return this.mutate({ mutation, variables: { merchant } }, 'updateMerchant');
  }

  public async createMerchant(merchant: Merchant): Promise<Merchant> {
    const mutation = gql`
      mutation createMerchant($merchant: MerchantInput) {
        createMerchant(merchant: $merchant) {
          id
          merchant_name
          notes
          insights
          is_autoconfigured
          affiliate_links {
            id
            link_template
            affiliate_link_status
            affiliate_network {
              id
              network_name
            }
            merchant {
              id
            }
          }
          merchant_domains {
            id
            merchant_id
            merchant_domain
            merchant_domain_status
          }
        }
      }
    `;
    return this.mutate({ mutation, variables: { merchant } }, 'createMerchant');
  }

  public async getAllMerchants(): Promise<Merchant[]> {
    const query = gql`
      query allMerchants {
        allMerchants {
          id
          merchant_name
        }
      }
    `;
    return this.query<Merchant[]>({ query }, 'allMerchants');
  }

  public async getMerchants(
    params: DataLoaderParams,
    searchByName: string
  ): Promise<QueryResponse<PaginatedResponse<Merchant>>> {
    const query = gql`
      query merchants(
        $first: Int!
        $page: Int!
        $filters: DataLoaderParams!
        $searchByName: String
      ) {
        merchants(
          first: $first
          page: $page
          filters: $filters
          searchByName: $searchByName
        ) {
          data {
            id
            merchant_name
            clicks
            views
            commission
            commission_opportunity
          }
          paginatorInfo {
            total
            perPage
            currentPage
            hasMorePages
          }
        }
      }
    `;
    return this.query<QueryResponse<PaginatedResponse<Merchant>>>({
      query,
      variables: { ...params, searchByName }
    });
  }

  public async getMerchant(
    params: MerchantDataLoaderParams
  ): Promise<Merchant> {
    const query = gql`
      query merchant($id: ID!) {
        merchant(id: $id) {
          id
          merchant_name
          notes
          insights
          is_autoconfigured
          affiliate_links {
            id
            link_template
            affiliate_link_status
            affiliate_network {
              id
              network_name
            }
            merchant {
              id
            }
          }
          merchant_domains {
            id
            merchant_id
            merchant_domain
            merchant_domain_status
          }
          affiliate_networks {
            id
            network_name
          }
        }
      }
    `;
    return this.query<Merchant>(
      { query, variables: { id: params.filters.merchantId } },
      'merchant'
    );
  }

  public async deleteMerchant(id: number): Promise<Merchant> {
    const mutation = gql`
      mutation deleteMerchant($id: ID!) {
        deleteMerchant(id: $id) {
          id
          merchant_name
        }
      }
    `;
    const variables = { id };
    return await this.mutate({ mutation, variables }, 'deleteMerchant');
  }

  public async deleteMerchantDomain(id: number): Promise<MerchantDomain> {
    const mutation = gql`
      mutation deleteMerchantDomain($id: ID!) {
        deleteMerchantDomain(id: $id) {
          id
          merchant_domain
        }
      }
    `;
    return await this.mutate(
      { mutation, variables: { id } },
      'deleteMerchantDomain'
    );
  }

  public async createMerchantDomain(
    merchantDomain: MerchantDomain
  ): Promise<MerchantDomain> {
    const mutation = gql`
      mutation createMerchantDomain($merchantDomain: MerchantDomainInput) {
        createMerchantDomain(merchantDomain: $merchantDomain) {
          id
          merchant_id
          merchant_domain
          merchant_domain_status
          created_at
          updated_at
        }
      }
    `;
    return await this.mutate(
      { mutation, variables: { merchantDomain } },
      'createMerchantDomain'
    );
  }
}

export const merchantSvc: MerchantService = new MerchantService();
